import { useEffect } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, ArrowRight, ArrowUpRight, Star, CheckCircle2 } from "lucide-react";
import { useLang } from "@/i18n";
import { awsServices } from "@/data/awsServices";
import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import { AwsCalculator } from "@/components/AwsCalculator";
import { LeadForm } from "@/components/LeadForm";
import CostOptSections from "@/components/CostOptSections";
import ServiceDeepDive from "@/components/ServiceDeepDive";
import { Reveal, MaskedLine, FadeStagger, FadeItem } from "@/components/Motion";
import { ICONS } from "@/pages/AwsHub";

const AwsServiceDetail = () => {
  const { slug } = useParams();
  const { t, lang } = useLang();
  const navigate = useNavigate();
  const service = awsServices.find((s) => s.slug === slug);

  useEffect(() => {
    if (!service) navigate("/aws", { replace: true });
  }, [service, navigate]);

  if (!service) return null;

  const c = service[lang];
  const Icon = ICONS[service.icon];
  const others = awsServices.filter((s) => s.slug !== slug);

  return (
    <div data-testid={`aws-service-${slug}`} className="bg-[#FAFAFD] text-[#353535] min-h-screen">
      <Navbar accent="#ff764a" />

      <section className="relative px-5 sm:px-8 pt-36 pb-16 sm:pt-44 overflow-hidden">
        <div className="absolute -top-32 right-10 h-[420px] w-[420px] rounded-full bg-[#ff764a]/[0.06] blur-[110px] pointer-events-none" />
        <div className="relative mx-auto max-w-[1400px]">
          <Link data-testid="service-back-link" to="/aws" className="group inline-flex items-center gap-2 font-jbmono text-[11px] uppercase tracking-[0.25em] text-[#999999] hover:text-[#353535] transition-colors mb-10">
            <ArrowLeft size={13} className="transition-transform duration-300 group-hover:-translate-x-1" /> {t.common.backToAws}
          </Link>
          <div className="flex flex-wrap items-center gap-4 mb-6">
            <span className="flex h-14 w-14 items-center justify-center rounded-2xl bg-[#ff764a] text-white">
              <Icon size={26} />
            </span>
            {service.popular && (
              <span className="inline-flex items-center gap-1.5 rounded-full border border-[#ff764a]/40 px-4 py-1.5 font-jbmono text-[10px] font-bold uppercase tracking-widest text-[#ff764a]">
                <Star size={10} /> {t.common.mostPopular}
              </span>
            )}
          </div>
          <h1 className="font-playfair font-medium leading-[1.05] text-[10vw] sm:text-[6vw] lg:text-[4.4vw] tracking-tight max-w-5xl">
            <MaskedLine delay={0.15}>{c.name}</MaskedLine>
          </h1>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.5 }}
            className="mt-6 font-playfair text-xl sm:text-2xl text-[#ff764a] max-w-3xl"
          >
            {c.tagline}
          </motion.p>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.65 }}
            className="mt-5 max-w-2xl text-sm sm:text-base text-[#999999] font-jakarta leading-relaxed"
          >
            {c.intro}
          </motion.p>
        </div>
      </section>

      {service.calculator && (
        <section className="px-5 sm:px-8 pb-20">
          <Reveal className="mx-auto max-w-[1400px]">
            <AwsCalculator />
          </Reveal>
        </section>
      )}

      {slug !== "aws-cost-optimization" && (
      <section className="px-5 sm:px-8 py-16 border-t border-black/10">
        <div className="mx-auto max-w-[1400px] flex flex-col gap-0">
          {c.blocks.map((b, i) => (
            <Reveal key={i} className="grid gap-6 lg:grid-cols-[100px_1fr_1.2fr] border-t border-black/10 first:border-t-0 py-12">
              <span className="font-jbmono text-sm text-[#ff764a]">({String(i + 1).padStart(2, "0")})</span>
              <h2 className="font-playfair text-2xl sm:text-3xl">{b.h}</h2>
              <div>
                <p className="text-sm sm:text-base text-[#999999] font-jakarta leading-relaxed">{b.p}</p>
                <ul className="mt-5 flex flex-col gap-2">
                  {b.list.map((li, j) => (
                    <li key={j} className="flex items-start gap-2.5 text-sm text-[#353535]/80 font-jakarta">
                      <CheckCircle2 size={15} className="mt-0.5 shrink-0 text-[#10B981]" /> {li}
                    </li>
                  ))}
                </ul>
              </div>
            </Reveal>
          ))}
        </div>
      </section>
      )}

      {slug !== "aws-cost-optimization" && (
      <section className="px-5 sm:px-8 py-16 border-t border-black/10">
        <div className="mx-auto max-w-[1400px]">
          <FadeStagger className="grid gap-px bg-black/10 rounded-2xl overflow-hidden sm:grid-cols-2 lg:grid-cols-4">
            {c.outcomes.map((o, i) => (
              <FadeItem key={i} className="bg-white p-7">
                <div className="font-jbmono text-xs text-[#ff764a] mb-3">0{i + 1}</div>
                <p className="text-sm font-jakarta text-[#353535]/85 leading-relaxed">{o}</p>
              </FadeItem>
            ))}
          </FadeStagger>
        </div>
      </section>
      )}

      {slug === "aws-cost-optimization" && <CostOptSections />}

      {service.deep && <ServiceDeepDive service={service} />}

      <section className="px-5 sm:px-8 py-16 border-t border-black/10">
        <div className="mx-auto max-w-[1400px]">
          <Reveal>
            <div className="font-jbmono text-[11px] uppercase tracking-[0.3em] text-[#999999] mb-8">{t.aws.servicesKicker}</div>
          </Reveal>
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-5">
            {others.map((s) => {
              const OIcon = ICONS[s.icon];
              return (
                <Link
                  key={s.slug}
                  to={`/aws/services/${s.slug}`}
                  data-testid={`service-related-${s.slug}`}
                  className="group rounded-xl border border-black/10 bg-white p-5 transition-all duration-300 hover:border-[#ff764a] hover:-translate-y-1"
                >
                  <OIcon size={18} className="text-[#ff764a]" />
                  <div className="mt-3 font-playfair text-sm font-semibold leading-snug">{s[lang].name}</div>
                  <ArrowUpRight size={13} className="mt-2 text-slate-400 transition-all duration-300 group-hover:text-[#ff764a] group-hover:rotate-45" />
                </Link>
              );
            })}
          </div>
        </div>
      </section>

      <section className="px-5 sm:px-8 py-20 border-t border-black/10">
        <div className="mx-auto max-w-[1400px] grid gap-12 lg:grid-cols-2 items-start">
          <Reveal>
            <h2 className="font-playfair text-3xl sm:text-4xl leading-tight">{c.name}</h2>
            <p className="mt-4 text-[#999999] font-jakarta max-w-md">{t.aws.ctaBody}</p>
          </Reveal>
          <Reveal delay={0.15}>
            <LeadForm defaultUnit="aws" service={slug} accent="#ff764a" />
          </Reveal>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default AwsServiceDetail;
